/**
 * Pure helpers for "show prices by group" / seat-based pricing route gating.
 * @see hooks/useShowPricesByGroup — React wiring only.
 */

/**
 * Normalizes a string for route comparison (lowercase, trimmed).
 * @param {string} value - City name or slug.
 * @returns {string}
 */
const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/**
 * Builds a comparable route key from origin and destination.
 * @param {string} origin - Origin city name or slug.
 * @param {string} destination - Destination city name or slug.
 * @returns {string} Key "origin-destination".
 */
const toRouteKey = (origin, destination) => `${normalize(origin)}-${normalize(destination)}`;

/**
 * Returns true if (origin, destination) or (destination, origin) matches any allowed route.
 * @param {string} origin - Current origin (cityName or slug).
 * @param {string} destination - Current destination (cityName or slug).
 * @param {{ origin: string, destination: string }[]} allowedRoutes - Allowed routes.
 * @returns {boolean}
 */
export function isRouteInAllowedList(origin, destination, allowedRoutes) {
  if (!origin || !destination) return false;
  if (!Array.isArray(allowedRoutes) || allowedRoutes.length === 0) return false;
  const allowed = new Set(allowedRoutes.map((r) => toRouteKey(r?.origin, r?.destination)));
  const key = toRouteKey(origin, destination);
  const keyReverse = toRouteKey(destination, origin);
  return allowed.has(key) || allowed.has(keyReverse);
}

/**
 * Extracts origin/destination name from a place (terminal/city) or fragment.
 * @param {Object} place - Object with cityName and/or name.
 * @returns {string}
 */
const getPlaceName = (place) => place?.cityName ?? place?.name ?? '';

/**
 * Gets origin/destination from a way (departs/returns) with fallback to first fragment.
 * @param {Object} way - departs or returns from purchase.
 * @returns {{ origin: string, destination: string }}
 */
const getWayRouteNames = (way) => {
  const first = way?.fragments?.[0];
  const origin = getPlaceName(way?.origin ?? first?.origin);
  const destination = getPlaceName(way?.destination ?? first?.destination);
  return { origin, destination };
};

/**
 * Gets origin/destination slugs from search state (Immutable or plain).
 * @param {Object} search - state.search.
 * @returns {{ origin: string, destination: string }}
 */
const getSearchSlugs = (search) => {
  const get = typeof search?.get === 'function' ? search.get.bind(search) : null;
  const originId = get ? get('originId') : search?.originId;
  const destinationId = get ? get('destinationId') : search?.destinationId;
  return {
    origin: originId != null ? normalize(originId) : '',
    destination: destinationId != null ? normalize(destinationId) : '',
  };
};

/**
 * Resolves config to: false (disabled), true (all routes), or array of allowed routes.
 * @param {Object} config - showPricesByGroupConfig from env.
 * @returns {false | true | Array<{ origin: string, destination: string }>}
 */
export const resolveShowPricesByGroupConfig = (config) => {
  if (!config || typeof config !== 'object') return false;
  if (config.enabledOnAllRoutes === true) return true;
  const list = Array.isArray(config.allowedRoutes) ? config.allowedRoutes : [];
  return list.length > 0 ? list : false;
};

/**
 * Returns true when current route is enabled for seat-based pricing according to env config.
 * @param {Object} params - Validation parameters.
 * @param {string} params.origin - Current origin.
 * @param {string} params.destination - Current destination.
 * @param {Object} params.showPricesByGroupConfig - Env config.
 * @returns {boolean}
 */
export const canUseShowPricesByGroupForRoute = ({
  origin,
  destination,
  showPricesByGroupConfig,
}) => {
  const resolved = resolveShowPricesByGroupConfig(showPricesByGroupConfig);
  if (resolved === false) return false;
  if (resolved === true) return true;
  return isRouteInAllowedList(origin, destination, resolved);
};

/**
 * Returns true if any of departs, returns or search route matches the allowed list.
 * @param {Object} routeNames - { departs, returns, search } each { origin, destination }.
 * @param {{ origin: string, destination: string }[]} allowedRoutes - Allowed routes.
 * @returns {boolean}
 */
const anyRouteMatches = (routeNames, allowedRoutes) => {
  const { departs, returns, search } = routeNames;
  const hasCompleteDepartsRoute = Boolean(departs.origin && departs.destination);
  const hasCompleteReturnsRoute = Boolean(returns.origin && returns.destination);
  const hasCompleteSearchRoute = Boolean(search.origin && search.destination);

  return (
    (hasCompleteDepartsRoute &&
      isRouteInAllowedList(departs.origin, departs.destination, allowedRoutes)) ||
    (hasCompleteReturnsRoute &&
      isRouteInAllowedList(returns.origin, returns.destination, allowedRoutes)) ||
    (hasCompleteSearchRoute &&
      isRouteInAllowedList(search.origin, search.destination, allowedRoutes))
  );
};

/**
 * True when purchase legs or search route match `allowedRoutes` (after config resolved to a list).
 * @param {object} purchase - Plain `{ departs, returns }` from purchase state.
 * @param {object} search - Redux search (Immutable or plain).
 * @param {Array<{ origin: string, destination: string }>} allowedRoutes - Non-empty allowlist.
 * @returns {boolean}
 */
export const purchaseMatchesShowPricesByGroupAllowedRoutes = (purchase, search, allowedRoutes) => {
  const departs = purchase?.departs || {};
  const returns = purchase?.returns || {};
  const routeNames = {
    departs: getWayRouteNames(departs),
    returns: getWayRouteNames(returns),
    search: getSearchSlugs(search),
  };
  return anyRouteMatches(routeNames, allowedRoutes);
};

/**
 * Whether seat-based pricing (zones) applies to a single purchase leg.
 * Same rules as {@link useShowPricesByGroup}, but origin/destination come only from that leg.
 *
 * @param {Object} way - `purchase.departs` or `purchase.returns` (plain object).
 * @param {Object} flags - Feature flags for the tenant/session.
 * @param {boolean} flags.useSeatBasedPricing - Whitelabel `USE_SEAT_BASED_PRICING`.
 * @param {boolean} flags.showPricesByGroupGB - GrowthBook `show_prices_by_group`.
 * @param {Object} flags.showPricesByGroupConfig - Env `showPricesByGroupConfig`.
 * @returns {boolean}
 */
export const isSeatBasedPricingEnabledForWay = (way, flags) => {
  const { useSeatBasedPricing, showPricesByGroupGB, showPricesByGroupConfig } = flags;
  if (!useSeatBasedPricing || !showPricesByGroupGB) return false;
  const { origin, destination } = getWayRouteNames(way || {});
  return canUseShowPricesByGroupForRoute({
    origin,
    destination,
    showPricesByGroupConfig,
  });
};
